import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Switch,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  FormGroup,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Tooltip,
  Card,
  CardContent,
  Divider,
  Stack,
  Snackbar,
  Container,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ContentCopy as CloneIcon,
  CheckCircle as CheckCircleIcon,
  DragIndicator as DragIcon,
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";

import {
  normalizeBillingCycles,
  durationLabel,
} from "../../utils/pricingPlanUtils";

const MUI_ICONS = {
  TrendingUp: "TrendingUp",
  Business: "Business",
  RocketLaunch: "RocketLaunch",
  Home: "Home",
  Star: "Star",
  WorkspacePremium: "WorkspacePremium",
};

const defaultBillingCycles = () => ({
  month: {
    label: "1 Month",
    months: 1,
    price: "",
    contactLimit: 0,
    chatbotNodeLimit: 0,
  },
});

const PricingPlansManager = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [error, setError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [dialogMode, setDialogMode] = useState("create"); // 'create' or 'edit' or 'clone'

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    features: [],
    newFeature: "",
    popular: false,
    icon: "TrendingUp",
    color: "#128C7E",
    active: true,
    isFree: false,
    billingCycles: defaultBillingCycles(),
  });

  // Fetch plans on component mount
  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(`${API_ENDPOINT}/root/pricing-plans`, {
        method: "GET",
        ...getHeader(),
      });
      const data = await response.json();

      if (response.ok) {
        setPlans(data.plans || []);
      } else {
        setError(data.message || "Failed to fetch plans");
      }
    } catch (err) {
      setError("Error connecting to server");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateDialog = () => {
    setDialogMode("create");
    setFormData({
      name: "",
      description: "",
      features: [],
      newFeature: "",
      popular: false,
      icon: "TrendingUp",
      color: "#128C7E",
      active: true,
      isFree: false,
      billingCycles: defaultBillingCycles(),
    });
    setFormError("");
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (plan) => {
    setDialogMode("edit");
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description,
      features: [...(plan.features || [])],
      newFeature: "",
      popular: plan.popular,
      icon: plan.icon,
      color: plan.color,
      active: plan.active,
      isFree: plan.isFree === true,
      billingCycles: normalizeBillingCycles(plan.billingCycles),
    });
    setFormError("");
    setOpenDialog(true);
  };

  // NEW FUNCTION: Handle Clone Plan
  const handleOpenCloneDialog = (plan) => {
    setDialogMode("clone");
    setEditingPlan(plan);
    setFormData({
      name: `${plan.name} (Copy)`,
      description: plan.description,
      features: [...(plan.features || [])],
      newFeature: "",
      popular: false, // Reset popular flag for cloned plan
      icon: plan.icon,
      color: plan.color,
      active: true, // Set active by default for cloned plan
      isFree: plan.isFree === true,
      billingCycles: normalizeBillingCycles(plan.billingCycles),
    });
    setFormError("");
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    if (saving) return;
    setOpenDialog(false);
    setEditingPlan(null);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBillingCycleChange = (cycle, field, value) => {
    setFormData((prev) => ({
      ...prev,
      billingCycles: {
        ...prev.billingCycles,
        [cycle]: {
          ...prev.billingCycles[cycle],
          [field]: value,
        },
      },
    }));
  };

  const handleAddBillingCycle = () => {
    setFormData((prev) => {
      let index = 1;
      while (Object.hasOwn(prev.billingCycles, `custom_${index}`)) index += 1;
      return {
        ...prev,
        billingCycles: {
          ...prev.billingCycles,
          [`custom_${index}`]: {
            label: "",
            months: "",
            price: "",
            contactLimit: 0,
            chatbotNodeLimit: 0,
          },
        },
      };
    });
  };

  const handleRemoveBillingCycle = (key) => {
    setFormData((prev) => ({
      ...prev,
      billingCycles: Object.fromEntries(
        Object.entries(prev.billingCycles).filter(
          ([cycleKey]) => cycleKey !== key
        )
      ),
    }));
  };

  const handleAddFeature = () => {
    if (formData.newFeature.trim()) {
      setFormData((prev) => ({
        ...prev,
        features: [...prev.features, prev.newFeature.trim()],
        newFeature: "",
      }));
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    if (saving) return;
    setFormError("");
    const entries = Object.entries(formData.billingCycles);
    if (!formData.name.trim() || !entries.length) {
      setFormError("Enter a plan name and add at least one billing option.");
      return;
    }
    const seenMonths = new Set();
    for (const [, cycle] of entries) {
      const months = Number(cycle.months);
      const price = Number(cycle.price);
      const contacts = Number(cycle.contactLimit);
      const chatbotNodes = Number(cycle.chatbotNodeLimit);
      if (
        !Number.isSafeInteger(months) ||
        months < 1 ||
        seenMonths.has(months)
      ) {
        setFormError(
          "Each option needs a unique, positive whole number of months."
        );
        return;
      }
      if (
        (!formData.isFree &&
          (String(cycle.price).trim() === "" ||
            !Number.isFinite(price) ||
            price <= 0 ||
            Math.abs(price * 100 - Math.round(price * 100)) > 0.000001)) ||
        String(cycle.contactLimit).trim() === "" ||
        !Number.isSafeInteger(contacts) ||
        contacts < 0 ||
        String(cycle.chatbotNodeLimit).trim() === "" ||
        !Number.isSafeInteger(chatbotNodes) ||
        chatbotNodes < 0
      ) {
        setFormError(
          formData.isFree
            ? "Enter non-negative whole numbers for the contact and chatbot node limits."
            : "Enter a positive price (up to 2 decimals) and non-negative whole numbers for the contact and chatbot node limits."
        );
        return;
      }
      seenMonths.add(months);
    }
    setSaving(true);
    try {
      const url =
        dialogMode === "create" || dialogMode === "clone"
          ? `${API_ENDPOINT}/root/pricing-plans`
          : `${API_ENDPOINT}/root/pricing-plans/${editingPlan._id}`;

      const method = dialogMode === "edit" ? "PUT" : "POST";

      // Prepare data for submission
      const submitData = {
        ...formData,
        name: formData.name.trim(),
        features: formData.features,
        billingCycles: Object.fromEntries(
          entries.map(([key, cycle]) => [
            key,
            {
              ...cycle,
              label: cycle.label.trim() || durationLabel(Number(cycle.months)),
              months: Number(cycle.months),
              price: formData.isFree ? 0 : Number(cycle.price),
              contactLimit: Number(cycle.contactLimit),
              chatbotNodeLimit: Number(cycle.chatbotNodeLimit),
            },
          ])
        ),
      };

      // Remove newFeature from submission
      delete submitData.newFeature;

      const response = await fetch(url, {
        method,
        ...getHeader(),
        body: JSON.stringify(submitData),
      });

      const data = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: data.message,
          severity: "success",
        });
        fetchPlans();
        setOpenDialog(false);
        setEditingPlan(null);
      } else {
        setFormError(data.message || "Operation failed");
        setSnackbar({
          open: true,
          message: data.message || "Operation failed",
          severity: "error",
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error connecting to server",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (planId, currentStatus) => {
    try {
      const response = await fetch(
        `${API_ENDPOINT}/root/pricing-plans/${planId}/toggle-active`,
        {
          method: "PUT",
          ...getHeader(),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: data.message,
          severity: "success",
        });
        fetchPlans();
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to update status",
          severity: "error",
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error connecting to server",
        severity: "error",
      });
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm("Are you sure you want to delete this plan?")) {
      return;
    }

    try {
      const response = await fetch(
        `${API_ENDPOINT}/root/pricing-plans/${planId}`,
        {
          method: "DELETE",
          ...getHeader(),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: data.message,
          severity: "success",
        });
        fetchPlans();
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to delete plan",
          severity: "error",
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error connecting to server",
        severity: "error",
      });
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(plans);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order in UI immediately for better UX
    const updatedPlans = items.map((item, index) => ({
      ...item,
      order: index,
    }));
    setPlans(updatedPlans);

    // Update order in backend
    try {
      const response = await fetch(
        `${API_ENDPOINT}/root/pricing-plans/${reorderedItem._id}/order`,
        {
          method: "PUT",
          ...getHeader(),
          body: JSON.stringify({ order: result.destination.index }),
        }
      );

      if (!response.ok) {
        // Revert if failed
        fetchPlans();
      }
    } catch (err) {
      // Revert if error
      fetchPlans();
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ mt: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h6">Pricing Plans Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
          sx={{ bgcolor: "primary.main" }}
        >
          Add New Plan
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <DragDropContext onDragEnd={handleDragEnd}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="50px">Order</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Plan Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Popular</TableCell>
                    <TableCell>Billing Cycles</TableCell>
                    <TableCell>Features</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <Droppable droppableId="plans">
                  {(provided) => (
                    <TableBody
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {plans.map((plan, index) => (
                        <Draggable
                          key={plan._id}
                          draggableId={plan._id}
                          index={index}
                        >
                          {(provided) => (
                            <TableRow
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              sx={{
                                bgcolor: plan.active
                                  ? "background.paper"
                                  : "action.disabledBackground",
                                opacity: plan.active ? 1 : 0.7,
                              }}
                            >
                              <TableCell {...provided.dragHandleProps}>
                                <DragIcon sx={{ cursor: "grab" }} />
                              </TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box
                                    width={40}
                                    height={40}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    borderRadius={1}
                                    bgcolor={plan.color}
                                    color="white"
                                  >
                                    {plan.icon}
                                  </Box>
                                  <Box>
                                    <Typography variant="subtitle1">
                                      {plan.name}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {plan.description}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={plan.isFree === true ? "Free" : "Paid"}
                                  color={
                                    plan.isFree === true ? "success" : "primary"
                                  }
                                  variant="outlined"
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={plan.active}
                                      onChange={() =>
                                        handleToggleActive(
                                          plan._id,
                                          plan.active
                                        )
                                      }
                                      color="primary"
                                    />
                                  }
                                  label={plan.active ? "Active" : "Inactive"}
                                />
                              </TableCell>
                              <TableCell>
                                {plan.popular && (
                                  <Chip
                                    label="Popular"
                                    color="primary"
                                    size="small"
                                    icon={<CheckCircleIcon />}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                <Box>
                                  {Object.entries(
                                    normalizeBillingCycles(plan.billingCycles)
                                  ).map(([cycle, data]) => (
                                    <Typography
                                      key={cycle}
                                      variant="caption"
                                      display="block"
                                    >
                                      {data.label ||
                                        durationLabel(
                                          data.months ??
                                            {
                                              month: 1,
                                              quarter: 3,
                                              halfYear: 6,
                                              year: 12,
                                            }[cycle] ??
                                            cycle
                                        )}
                                      : {plan.isFree === true
                                        ? "Free"
                                        : `₹${data.price}`} {" · "}
                                      {Number(
                                        data.contactLimit
                                      ).toLocaleString()} contacts {" · "}
                                      {Number(
                                        data.chatbotNodeLimit
                                      ).toLocaleString()} chatbot nodes
                                    </Typography>
                                  ))}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" noWrap>
                                  {plan.features?.length || 0} features
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Edit">
                                  <IconButton
                                    onClick={() => handleOpenEditDialog(plan)}
                                    color="primary"
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Clone">
                                  <IconButton
                                    onClick={() => handleOpenCloneDialog(plan)}
                                    color="info"
                                  >
                                    <CloneIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    onClick={() => handleDelete(plan._id)}
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </TableBody>
                  )}
                </Droppable>
              </Table>
            </TableContainer>
          </DragDropContext>
        </CardContent>
      </Card>

      {/* Create/Edit/Clone Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === "create" && "Create New Pricing Plan"}
          {dialogMode === "edit" && "Edit Pricing Plan"}
          {dialogMode === "clone" && "Clone Pricing Plan"}
        </DialogTitle>
        <DialogContent>
          <Box
            component="fieldset"
            disabled={saving}
            sx={{ mt: 2, border: 0, p: 0, minWidth: 0 }}
          >
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Plan Name"
                  value={formData.name}
                  onChange={(e) => handleFormChange("name", e.target.value)}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Icon</InputLabel>
                  <Select
                    value={formData.icon}
                    label="Icon"
                    onChange={(e) => handleFormChange("icon", e.target.value)}
                  >
                    {Object.entries(MUI_ICONS).map(([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) =>
                    handleFormChange("description", e.target.value)
                  }
                  margin="normal"
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Color"
                  type="color"
                  value={formData.color}
                  onChange={(e) => handleFormChange("color", e.target.value)}
                  margin="normal"
                  InputProps={{
                    style: { height: "56px" },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Plan Type</InputLabel>
                  <Select
                    value={formData.isFree ? "free" : "paid"}
                    label="Plan Type"
                    onChange={(e) =>
                      handleFormChange("isFree", e.target.value === "free")
                    }
                  >
                    <MenuItem value="paid">Paid plan</MenuItem>
                    <MenuItem value="free">Free plan</MenuItem>
                  </Select>
                </FormControl>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.popular}
                        onChange={(e) =>
                          handleFormChange("popular", e.target.checked)
                        }
                      />
                    }
                    label="Mark as Popular Plan"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.active}
                        onChange={(e) =>
                          handleFormChange("active", e.target.checked)
                        }
                      />
                    }
                    label="Active"
                  />
                </FormGroup>
              </Grid>

              {/* Each plan may offer any number of month-based billing options. */}
              <Grid item xs={12}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={1}
                >
                  <Typography variant="h6">Billing Options</Typography>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddBillingCycle}
                  >
                    Add Duration
                  </Button>
                </Box>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Add only the durations this plan offers. For a monthly-only
                  plan, keep one option with 1 month. Each option controls its
                  contact and chatbot-node limits. Paid-plan prices are for the
                  entire duration, excluding GST.
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(formData.billingCycles).map(
                    ([cycle, data]) => (
                      <Grid item xs={12} sm={6} key={cycle}>
                        <Paper
                          variant="outlined"
                          sx={{ p: 2, borderRadius: 2 }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography fontWeight={600}>
                              {data.label || "Custom Duration"}
                            </Typography>
                            <IconButton
                              aria-label="Remove duration"
                              color="error"
                              disabled={
                                Object.keys(formData.billingCycles).length <= 1
                              }
                              onClick={() => handleRemoveBillingCycle(cycle)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                          <TextField
                            fullWidth
                            label="Display Label (optional)"
                            value={data.label}
                            onChange={(e) =>
                              handleBillingCycleChange(
                                cycle,
                                "label",
                                e.target.value
                              )
                            }
                            margin="dense"
                          />
                          <TextField
                            fullWidth
                            required
                            label="Duration in Months"
                            type="number"
                            value={data.months}
                            inputProps={{ min: 1, step: 1 }}
                            onChange={(e) =>
                              handleBillingCycleChange(
                                cycle,
                                "months",
                                e.target.value
                              )
                            }
                            margin="dense"
                          />
                          <TextField
                            fullWidth
                            required={!formData.isFree}
                            label="Price excluding GST (₹)"
                            type="number"
                            value={formData.isFree ? 0 : data.price}
                            disabled={formData.isFree}
                            inputProps={{ min: 0.01, step: 0.01 }}
                            onChange={(e) =>
                              handleBillingCycleChange(
                                cycle,
                                "price",
                                e.target.value
                              )
                            }
                            margin="dense"
                            helperText={
                              formData.isFree
                                ? "Free plans are always saved with a ₹0 price."
                                : "Price for the full selected duration."
                            }
                          />
                          <TextField
                            fullWidth
                            required
                            label="Contact Limit"
                            type="number"
                            value={data.contactLimit}
                            inputProps={{ min: 0, step: 1 }}
                            onChange={(e) =>
                              handleBillingCycleChange(
                                cycle,
                                "contactLimit",
                                e.target.value
                              )
                            }
                            margin="dense"
                          />
                          <TextField
                            fullWidth
                            required
                            label="Chatbot Node Limit"
                            type="number"
                            value={data.chatbotNodeLimit}
                            inputProps={{ min: 0, step: 1 }}
                            onChange={(e) =>
                              handleBillingCycleChange(
                                cycle,
                                "chatbotNodeLimit",
                                e.target.value
                              )
                            }
                            margin="dense"
                            helperText="Maximum chatbot flow nodes included."
                          />
                        </Paper>
                      </Grid>
                    )
                  )}
                </Grid>
              </Grid>

              {/* Features */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Features
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  <Box display="flex" gap={1}>
                    <TextField
                      fullWidth
                      label="New Feature"
                      value={formData.newFeature}
                      onChange={(e) =>
                        handleFormChange("newFeature", e.target.value)
                      }
                      onKeyPress={(e) =>
                        e.key === "Enter" && handleAddFeature()
                      }
                    />
                    <Button variant="outlined" onClick={handleAddFeature}>
                      Add
                    </Button>
                  </Box>
                  <Box>
                    {formData.features.map((feature, index) => (
                      <Chip
                        key={index}
                        label={feature}
                        onDelete={() => handleRemoveFeature(index)}
                        sx={{ m: 0.5 }}
                      />
                    ))}
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button
            disabled={saving}
            onClick={handleSubmit}
            variant="contained"
            color="primary"
          >
            {saving && <CircularProgress size={16} sx={{ mr: 1 }} />}
            {dialogMode === "create" && "Create Plan"}
            {dialogMode === "edit" && "Save Changes"}
            {dialogMode === "clone" && "Create Clone"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PricingPlansManager;
